/**
 * Message Share Dialog
 *
 * Creates and displays public links for individual chat messages.
 *
 * Primary responsibilities:
 * - Update message sharing state
 * - Render a copyable public message URL
 * - Track assistant output share outcomes
 *
 * Dependencies:
 * - Chat context
 * - Message database helper
 */

import { useContext, useEffect, useState } from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import { updateMessage } from '@/db/messages'
import { IconWorld } from '@tabler/icons-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import { trackMeaningfulOutcomeCompleted } from '@/lib/analytics/meaningful-outcome'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'

interface MessageShareDialogProps {
  message: {
    id: string
    content: string
    sharing?: string
  }
  isAssistant?: boolean
  open: boolean
  setOpen: (open: boolean) => void
}

export function MessageShareDialog({
  message,
  isAssistant = false,
  open,
  setOpen
}: MessageShareDialogProps) {
  const { t } = useTranslation()
  const { selectedChat } = useContext(ChatbotUIChatContext)
  const [isLoading, setIsLoading] = useState(false)
  const [shareUrl, setShareUrl] = useState('')
  const isGroupChatMessage = Boolean(selectedChat?.is_group_chat)

  useEffect(() => {
    if (open && message.sharing === 'public') {
      setShareUrl(`${window.location.origin}/message/share/${message.id}`)
    }
  }, [open, message])

  const handleShare = async () => {
    if (!message) return

    if (isGroupChatMessage) {
      toast.error(t('Group chat messages cannot be shared publicly'))
      return
    }

    try {
      setIsLoading(true)

      await updateMessage(message.id, {
        sharing: 'public'
      } as any)

      setShareUrl(`${window.location.origin}/message/share/${message.id}`)
      if (isAssistant) {
        void trackMeaningfulOutcomeCompleted({
          surface: 'chat',
          outcome_type: 'saved_output',
          qualifying_action: 'shared',
          chat_id: selectedChat?.id ?? undefined,
          message_id: message.id,
          workspace_id: selectedChat?.workspace_id ?? undefined,
          source: 'message_share_dialog'
        })
      }
      toast.success(t('Message shared successfully'))
    } catch (error) {
      console.error('Error sharing message:', error)
      toast.error(t('Error sharing message'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('Share Message')}</DialogTitle>
          <DialogDescription>
            {isGroupChatMessage
              ? t('Group chat messages cannot be shared publicly.')
              : t('Create a shareable link for this message.')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col space-y-4">
          {isGroupChatMessage ? (
            <p className="text-muted-foreground text-sm">
              {t('Group chat messages cannot be shared publicly.')}
            </p>
          ) : shareUrl ? (
            <div className="flex min-w-0 items-center space-x-2">
              <div className="min-w-0 grow break-all rounded-md bg-muted px-3 py-2 text-sm text-foreground">
                {shareUrl}
              </div>
              <CopyButton value={shareUrl} className="size-10 shrink-0" />
            </div>
          ) : (
            <Button
              className="w-full"
              onClick={handleShare}
              loading={isLoading}
              disabled={isLoading}
            >
              <IconWorld className="mr-2 size-4" />
              {t('Share Message')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
